import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { IconClock, IconTag, IconX } from '@tabler/icons-react';
import { TeeButton } from '@/components/ui/tee-button';
import { TeeLoader } from '@/components/ui/tee-loader';
import { IssueStatusTimeline } from '@/pages/issues/components/IssueStatusTimeline';
import { issueNoticeService } from '@/services/issue-notice.service';
import { IssueSeverityBadge } from './IssueSeverityBadge';
import { formatClock, formatDay, noticeZone } from './issue-notice-meta';

interface Props {
  noticeId: string | null;
  onClose: () => void;
}

/**
 * Sign-in page version of the detail page: the latest status, workaround and
 * updates, read only. Long content scrolls inside the dialog.
 */
export function PublicIssueDetailModal({ noticeId, onClose }: Props) {
  const { t } = useTranslation();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['issue-notices', 'public-detail', noticeId],
    queryFn: () => issueNoticeService.publicDetail(noticeId as string),
    enabled: !!noticeId,
    refetchInterval: 60_000,
    retry: false,
  });
  const notFound = (error as { response?: { status?: number } } | null)?.response?.status === 404;

  const zone = data ? noticeZone(data) : undefined;
  const at = (iso: string) => `${formatDay(iso, zone)}, ${formatClock(iso, zone)}`;

  return (
    <DialogPrimitive.Root open={!!noticeId} onOpenChange={(open) => !open && onClose()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[80] bg-black/50" />
        <DialogPrimitive.Content
          className="fixed left-1/2 top-1/2 z-[80] flex max-h-[85vh] w-[calc(100%-32px)] max-w-[720px] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-[10px] border border-[var(--border-default)] bg-[var(--background-page-content)] shadow-lg focus:outline-none"
          aria-describedby={undefined}
        >
          <div className="flex items-start gap-3 border-b border-[var(--border-default)] px-5 py-4">
            <div className="min-w-0 flex-1">
              {data && (
                <div className="mb-2 flex flex-wrap items-center gap-2">
                  <IssueSeverityBadge severity={data.severity} />
                  <span className="inline-flex items-center gap-1.5 text-[13px] font-medium text-[var(--text-content-sub-title)]">
                    <IconTag size={14} aria-hidden className="text-[var(--color-forest)]" />
                    {data.categoryName}
                  </span>
                </div>
              )}
              <DialogPrimitive.Title className="break-words text-heading-h4 font-semibold text-[var(--text-content-title)]">
                {data?.title ?? t('issues.viewDetails')}
              </DialogPrimitive.Title>
              {data && (
                <p className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px] text-[var(--text-content-sub-title)]">
                  <IconClock size={14} aria-hidden className="text-[var(--color-forest)]" />
                  <span>{t('issues.window.startedAt', { time: at(data.startsAt) })}</span>
                  <span aria-hidden>·</span>
                  <span>{data.endsAt ? t('issues.window.endsAt', { time: at(data.endsAt) }) : t('issues.window.untilResolved')}</span>
                </p>
              )}
            </div>
            <TeeButton variant="ghost" size="s" aria-label={t('common.close')} onClick={onClose}>
              <IconX size={20} className="text-[var(--text-content-sub-title)]" />
            </TeeButton>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5">
            {isLoading && <TeeLoader size="md" />}
            {isError && (
              <p className="text-[14px] text-[var(--text-content-sub-title)]">
                {t(notFound ? 'issues.public.noLongerActive' : 'issues.public.loadFailed')}
              </p>
            )}
            {data && <IssueStatusTimeline notice={data} />}
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
